import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, NgZone } from '@angular/core';
import { ErrorDialogService } from './modal/error-dialog.service';
import { InfoDialogService } from './modal/info-dialog.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
	BAD_GATEWAY_STATUS: number = 502;

	constructor(
    private errorDialogService: ErrorDialogService,
    private infoDialogService: InfoDialogService,
    private zone: NgZone
	) {}

	handleError(error: any) {
    let message = 'Unknown error';

    if (error instanceof Error) {
      message = `${error.name}: ${error.message}\nStack:\n${error.stack}`;
    } else if (typeof error === 'string') {
      message = error;
    } else {
      try {
        message = JSON.stringify(error, null, 2);
      } catch {
        message = String(error);
      }
    }
	  console.log(`GlobalErrorHandler: ${message}`);
    if (GlobalErrorHandler.checkIfTokenRefreshError(error)) {
      let msg: string = "Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.";
		  var businessError = { message: msg, errorCode: "SESSION_LOST"};
		  this.zone.run(() => this.errorDialogService.openDialog(businessError));
      return
		}

		if (!(error instanceof HttpErrorResponse)) {
			console.log(error);
		} else if (error.status === this.BAD_GATEWAY_STATUS) {
		  console.log("[info] bad gateway");
    } else if (GlobalErrorHandler.checkIfErrorIsUnauthorized(error)) {
      let msg: string = "Beim Aufrufen der URL " + error.url + " ist leider ein Fehler aufgetreten. Bitte versuchen Sie es" +
        "erneut. Sollte das Problem weiterhin bestehen, melden Sie sich bitte ab und anschließend wieder an.";
		  var businessError = { message: msg, errorCode: "SESSION_LOST"};
		  this.zone.run(() => this.errorDialogService.openDialog(businessError));
      return
		} else if (this.checkIfErrorIsNotFound(error)) {
      let msg: string = "Die angeforderte Seite/der Inhalt unter der URL " + error.url + " ist nicht verfügbar bzw." +
          " konnte nicht aufgerufen werden. Bitte melden Sie das Problem umgehend den eAkte+/JADE Verantwortlichen. Vielen Dank.";
		  var businessError = { message: msg, errorCode: "NOT_FOUND"};
		  this.zone.run(() => this.errorDialogService.openDialog(businessError));
      return
		} else if (this.checkIfTimeout(error)) {
      let msg: string = "Das Aufrufen der angegebenen Inhalte unter der URL " + error.url + " hat zu lange gedauert und" +
       " wurde deshalb abgebrochen. Bitte versuchen Sie es erneut.";
		  let businessError = { message: msg, errorCode: "TIMEOUT"};
		  this.zone.run(() => this.errorDialogService.openDialog(businessError));
      return
		} else if (this.checkIfJadeNotOpeningStatus(error)) {
      let msg: string = "Das Dokument kann nicht geöffnet/heruntergeladen werden, da bereits während des Uploadvorgangs technische Probleme aufgetreten sind. Bitte versuche den Upload der Datei erneut oder kontaktiere den Lade-User des Dokuments.";
		  // var businessError = { message: msg, errorCode: "Jade"};
		  // this.zone.run(() => this.errorDialogService.openDialog(businessError));
      let details: string = `Jade`
			var businessError2 = { message: msg, details, title: "Fehlermeldung: Datei-Upload fehlgeschlagen:"};			
      this.zone.run(() => this.infoDialogService.openDialog(businessError2));

      return
		} else if (this.checkIfJadeOtherError(error)) {
      let msg: string = `Jade error: ${message}`;
		  var businessError = { message: msg, errorCode: "Jade"};
		  this.zone.run(() => this.errorDialogService.openDialog(businessError));
      return
		}
    //default
    {
      let msg: string = "Die angeforderte Seite/der Inhalt unter der URL ist nicht verfügbar bzw." +
          " konnte nicht aufgerufen werden. Bitte melden Sie das Problem umgehend den eAkte+/JADE Verantwortlichen. Vielen Dank.";
		  let techincalError = { message: msg, errorCode: `TECHNICAL ERROR: ${message}`};
		  this.zone.run(() => this.errorDialogService.openDialog(techincalError));
    }
	}

  public static getErrorDetails(errorObject: any): any {
    return errorObject?.error?.errorDetails ?? errorObject?.error?.details;
  }

  public static checkIfErrorIsUnauthorized(errorObject: any): boolean {
    const errorDetails = GlobalErrorHandler.getErrorDetails(errorObject);
    console.log(`checkIfErrorIsUnauthorized: errorObject?.status=${errorObject?.status},
      errorObject?.error?.message=${errorObject?.error?.message},
      errorDetails?.body?.message=${errorDetails?.body?.message},
      errorObject?.error?.message=${errorObject?.error?.message},
      errorDetails?.statusCode=${errorDetails?.statusCode}
      `);
    return (
      errorObject?.status === 401 &&
      errorObject?.error?.message === 'Unauthorized'
    ) 
    // || (errorObject?.status === 403
    //      && !(errorObject?.error?.errorDetails?.body?.message === 'Only documents in PROCESSING_FINISHED or LEGAL_HOLD can be downloaded.')
    //      && !(errorObject?.error?.message === 'Jade error: Only documents in PROCESSING_FINISHED or LEGAL_HOLD can be downloaded.')         
    //     )  
    || errorDetails?.statusCode === 401
    || errorObject?.status === 0;
  }

  public static checkIfTokenRefreshError(errorObject: any): boolean {
    return (GlobalErrorHandler.getErrorMessage(errorObject) === 'Unable to refresh token, logging out.');
  }

  public static getErrorMessage(error: any): string {
    if (error instanceof Error) {
      // Standard JavaScript Error
      return error.message;
    } else if (typeof error === 'string') {
      return error;
    } else if (error && typeof error === 'object') {
      // Maybe it's an HTTP error or custom error object
      if (error.message) return error.message;
      if (error.error && typeof error.error === 'string') return error.error;
      if (error.error && error.error.message) return error.error.message;
    }
    return 'An unknown error occurred.';
  }

  // The backend signals a non-openable document status with 403 (legacy) or 409/CONFLICT_ERROR (current).
  static readonly JADE_NOT_OPENING_STATUSES: number[] = [403, 409];
  static readonly JADE_NOT_OPENING_MESSAGE: string = 'Only documents in PROCESSING_FINISHED or LEGAL_HOLD can be downloaded.';

  checkIfJadeNotOpeningStatus(errorObject: any): boolean {
    const errorDetails = GlobalErrorHandler.getErrorDetails(errorObject);
    console.log(`checkIfJadeNotOpeningStatus: errorObject?.status=${errorObject?.status},
      errorObject?.error?.message=${errorObject?.error?.message},
      errorDetails?.body?.message=${errorDetails?.body?.message},
      errorDetails?.body?.errorDetails?.description=${errorDetails?.body?.errorDetails?.description},
      errorDetails?.statusCode=${errorDetails?.statusCode}
      `);
    const statuses = GlobalErrorHandler.JADE_NOT_OPENING_STATUSES;
    const statusMatches = statuses.includes(errorObject?.status)
      || statuses.includes(errorDetails?.statusCode);

    // The explanatory text sits in different places depending on the backend version.
    const texts: string[] = [
      errorDetails?.body?.errorDetails?.description,
      errorDetails?.description,
      errorDetails?.body?.message,
      errorObject?.error?.message
    ].filter((text: any) => typeof text === 'string');

    const messageMatches = texts.some((text: string) => text.includes(GlobalErrorHandler.JADE_NOT_OPENING_MESSAGE));

    return statusMatches && messageMatches;
  }

  checkIfJadeOtherError(errorObject: any): boolean {
    const errorDetails = GlobalErrorHandler.getErrorDetails(errorObject);
    console.log(`checkIfJadeOtherError: errorObject?.status=${errorObject?.status},
      errorObject?.error?.message=${errorObject?.error?.message},
      errorDetails?.body?.message=${errorDetails?.body?.message},
      errorObject?.error?.message=${errorObject?.error?.message},
      errorDetails?.statusCode=${errorDetails?.statusCode}
      `);
    return (JSON.stringify(errorObject).includes('Jade') && !this.checkIfJadeNotOpeningStatus(errorObject))         
  }

  checkIfErrorIsNotFound(error: any): boolean {
    return  error?.status === 404;
  }

  checkIfTimeout(error: any) : boolean {
    return (
      error?.status === 504 
      || GlobalErrorHandler.getErrorDetails(error)?.status === 504
      //&& error?.error?.message === 'Endpoint request timed out'
    );
  }
}
